import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { paths } from '@/utils/path'

import {
    getRackColumns,
    getRackFilterItems,
    getRackFilterToggles,
} from '../components/Racks/rack-list-config'
import { getRacks } from '../services'

interface RackListFilters {
    status: string
    location_id: string
}

const FILTER_DEFAULTS: RackListFilters = {
    status: '',
    location_id: '',
}

export const useRackList = () => {
    const navigate = useNavigate()
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<RackListFilters>({
        defaults: { sort_by: 'created_at', sort_order: 'desc' },
        filterDefaults: FILTER_DEFAULTS,
    })

    const { control } = useForm<RackListFilters>({
        defaultValues: filters,
    })

    const fetchFn = useCallback(() => getRacks(queryParams), [queryParams])

    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_RACKS, queryParams],
        queryFn: fetchFn,
        placeholderData: true,
    })

    const columns = useMemo(() => getRackColumns(), [])
    const filterItems = useMemo(() => getRackFilterItems(), [])
    const filterToggles = useMemo(() => getRackFilterToggles(), [])

    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    const handleCreate = useCallback(
        () => navigate(`${paths.warehouseRacks}/new`),
        [navigate]
    )

    return {
        racks: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),

        control,
        setFilter,
        params,
        searchInputProps,

        columns,
        filterItems,
        filterToggles,

        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        handleCreate,
        refetch,
    }
}
